// =============================================================================
// OPENSTREETMAP EMBED — Free, Cookieless Map Component (CartoDB Positron Light)
// =============================================================================
// Embeds a clean, privacy-first interactive map using CartoDB Positron (Light)
// based on OpenStreetMap tile data with custom styled pin and zero tracking.
// =============================================================================

import { useState, useMemo } from 'react';
import { MapPinIcon, ExternalLinkIcon } from '@webzite/core';

export interface OpenStreetMapEmbedProps {
  /** The fallback OpenStreetMap embed URL */
  embedUrl?: string;
  /** Restaurant latitude */
  latitude?: number;
  /** Restaurant longitude */
  longitude?: number;
  /** Direct link to open in Google Maps app */
  googleMapsLink?: string;
  /** Direct link to open in Apple Maps app */
  appleMapsLink?: string;
  /** Restaurant name / title for accessibility */
  title?: string;
  /** Restaurant address subtitle */
  address?: string;
  /** Optional custom button label for click-to-load */
  loadButtonText?: string;
  /** Label for Google Maps external link */
  openGoogleMapsText?: string;
  /** Label for Apple Maps external link */
  openAppleMapsText?: string;
  /** Fallback placeholder image URL before click */
  previewImageUrl?: string;
  /** Visual theme variant: 'white' (CartoDB Positron Light) or 'standard' */
  variant?: 'white' | 'standard';
}

export function OpenStreetMapEmbed({
  embedUrl,
  latitude = 48.8317072,
  longitude = 2.3290765,
  googleMapsLink,
  appleMapsLink,
  title = 'La Table du Prince',
  address = '8 Rue Mouton-Duvernet, 75014 Paris',
  loadButtonText = 'Load Interactive Map',
  openGoogleMapsText = 'Open in Google Maps',
  openAppleMapsText = 'Open in Apple Maps',
  previewImageUrl,
  variant = 'white',
}: OpenStreetMapEmbedProps) {
  const [isLoaded, setIsLoaded] = useState(false);

  // CartoDB Positron (Light) HTML template
  const positronSrcDoc = useMemo(() => {
    return `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" />
  <script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"></script>
  <style>
    html, body, #map {
      width: 100%;
      height: 100%;
      margin: 0;
      padding: 0;
      background-color: #f7f5f0;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }
    .leaflet-container {
      background: #f7f5f0;
    }
    .custom-pin {
      width: 28px;
      height: 28px;
      background: #72222e;
      border: 2.5px solid #ffffff;
      border-radius: 50% 50% 50% 0;
      transform: rotate(-45deg);
      box-shadow: 0 3px 8px rgba(0,0,0,0.3);
      position: relative;
    }
    .custom-pin::after {
      content: '';
      width: 10px;
      height: 10px;
      background: #b38f46;
      border-radius: 50%;
      position: absolute;
      top: 6.5px;
      left: 6.5px;
    }
    .leaflet-popup-content-wrapper {
      background: #ffffff;
      color: #241c16;
      border-radius: 8px;
      box-shadow: 0 4px 14px rgba(0,0,0,0.15);
      padding: 4px 6px;
    }
    .leaflet-popup-content {
      margin: 10px 14px;
      font-size: 13px;
      line-height: 1.4;
    }
    .leaflet-popup-content strong {
      color: #72222e;
      font-size: 14px;
    }
    .leaflet-control-attribution {
      background: rgba(255, 255, 255, 0.85) !important;
      font-size: 10px !important;
    }
  </style>
</head>
<body>
  <div id="map"></div>
  <script>
    var map = L.map('map', {
      zoomControl: true,
      scrollWheelZoom: false,
      attributionControl: true
    }).setView([${latitude}, ${longitude}], 16);

    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions" target="_blank">CARTO</a>',
      subdomains: 'abcd',
      maxZoom: 19
    }).addTo(map);

    var pinIcon = L.divIcon({
      className: 'custom-pin-wrapper',
      html: '<div class="custom-pin"></div>',
      iconSize: [28, 28],
      iconAnchor: [14, 28],
      popupAnchor: [0, -28]
    });

    var marker = L.marker([${latitude}, ${longitude}], { icon: pinIcon }).addTo(map);
    marker.bindPopup('<strong>${title.replace(/'/g, "\\'")}</strong><br/>${address.replace(/'/g, "\\'")}').openPopup();
  </script>
</body>
</html>`;
  }, [latitude, longitude, title, address]);

  return (
    <div
      className="map-embed-container"
      style={{
        position: 'relative',
        width: '100%',
        minHeight: '350px',
        borderRadius: 'var(--radius-lg, 8px)',
        overflow: 'hidden',
        backgroundColor: 'var(--color-surface, #ffffff)',
        border: '1px solid var(--color-border, #d9cfc2)',
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      {isLoaded ? (
        variant === 'white' ? (
          <iframe
            title={title}
            srcDoc={positronSrcDoc}
            width="100%"
            height="350"
            style={{
              border: 0,
              display: 'block',
              width: '100%',
            }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        ) : (
          <iframe
            title={title}
            src={
              embedUrl ??
              `https://www.openstreetmap.org/export/embed.html?bbox=${longitude - 0.005},${latitude - 0.003},${longitude + 0.005},${latitude + 0.003}&layer=mapnik&marker=${latitude},${longitude}`
            }
            width="100%"
            height="350"
            style={{
              border: 0,
              display: 'block',
              width: '100%',
            }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        )
      ) : (
        <div
          className="map-embed-placeholder"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '350px',
            padding: 'var(--space-6, 24px)',
            textAlign: 'center',
            backgroundImage: previewImageUrl ? `url(${previewImageUrl})` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setIsLoaded(true)}
            style={{
              backgroundColor: 'var(--color-surface, #ffffff)',
              boxShadow: 'var(--shadow-sm, 0 1px 3px rgba(0,0,0,0.1))',
              gap: '8px',
            }}
          >
            <MapPinIcon width={16} height={16} />
            <span>{loadButtonText}</span>
          </button>

          {(googleMapsLink || appleMapsLink) && (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: 'var(--space-3, 12px)',
                marginTop: 'var(--space-4, 16px)',
              }}
            >
              {googleMapsLink && (
                <a
                  href={googleMapsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-ghost btn-sm"
                  style={{ fontSize: 'var(--text-xs, 12px)', gap: '4px' }}
                >
                  <span>{openGoogleMapsText}</span>
                  <ExternalLinkIcon width={12} height={12} />
                </a>
              )}
              {appleMapsLink && (
                <a
                  href={appleMapsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-ghost btn-sm"
                  style={{ fontSize: 'var(--text-xs, 12px)', gap: '4px' }}
                >
                  <span>{openAppleMapsText}</span>
                  <ExternalLinkIcon width={12} height={12} />
                </a>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
